import React, { useState, useMemo, useEffect } from 'react';
import ToolsBanner from './components/ToolsBanner';
import Header from './components/Header';
import Stats from './components/Stats';
import UpcomingWatchlist from './components/UpcomingWatchlist';
import Legend from './components/Legend';
import ResourcesSection from './components/ResourcesSection';
import HowItWorks from './components/HowItWorks';
import CompanionResources from './components/CompanionResources';
import SearchFilters from './components/SearchFilters';
import CategoryTabs from './components/CategoryTabs';
import ViewModeSelector from './components/ViewModeSelector';
import SortingControls from './components/SortingControls';
import StateCard from './components/StateCard';
import StateModal from './components/StateModal';
import TableView from './components/TableView';
import TimelineView from './components/TimelineView';
import Footer from './components/Footer';
import FirstTimeModal from './components/FirstTimeModal';
import { statesData, allStates } from './data/statesData';
import { allInternationalLaws } from './data/internationalData';

import './index.css';
import entities from './data/entities.json';

// Entity pages are prerendered at /law/, /state/, /country/ and /category/ paths (scripts/prerender.mjs); the app
// opens the matching filter there and keeps the clean URL while the filter still matches.
const entityFromPath = () => {
  const m = window.location.pathname.match(/^\/(law|state|country|category)\/([a-z0-9-]+)\/?$/);
  if (!m) return null;
  const [, kind, slug] = m;
  if (kind === 'law') { const l = entities.laws.find((x) => x.slug === slug); return l ? { tab: 'all', q: l.lawName } : null; }
  if (kind === 'state') { const st = entities.states.find((x) => x.slug === slug); return st ? { tab: 'all', q: st.name } : null; }
  if (kind === 'country') { const c = entities.countries.find((x) => x.slug === slug); return c ? { tab: 'international', q: c.name } : null; }
  const cat = entities.categories.find((x) => x.slug === slug); return cat ? { tab: cat.id, q: '' } : null;
};
const initialEntity = entityFromPath();

// ── CSV export utility ──────────────────────────────────────────────────────
function exportToCSV(data, filename) {
  const headers = ['Jurisdiction', 'Law Name', 'Category', 'Status', 'Effective Date', 'Age Range', 'Key Requirements'];
  const rows = data.map(law => [
    law.state || law.country || '',
    law.lawName,
    law.category,
    law.status,
    law.effectiveDate,
    law.ageRange,
    (law.keyRequirements || []).join(' | ')
  ]);
  const csv = [headers, ...rows]
    .map(row => row.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const VALID_TABS     = new Set(['all','federal','consumerPrivacy','socialMedia','aadc','appStore','ageVerification','chatbot','international']);
const VALID_STATUSES = new Set(['all','active','challenged','pending','blocked']);
const VALID_VIEWS    = new Set(['cards','table','timeline']);
const VALID_SORTS    = new Set(['state','effectiveDate','status','category']);

function App() {
  // ── Read initial state from URL ────────────────────────────────────────
  const initParams = () => new URLSearchParams(window.location.search);

  const [activeTab, setActiveTab] = useState(() => {
    const v = initParams().get('tab'); return VALID_TABS.has(v) ? v : (initialEntity ? initialEntity.tab : 'all');
  });
  const [selectedStatus, setSelectedStatus] = useState(() => {
    const v = initParams().get('status'); return VALID_STATUSES.has(v) ? v : 'all';
  });
  const [searchTerm, setSearchTerm] = useState(() => initParams().get('q') || (initialEntity ? initialEntity.q : ''));
  const [selectedState, setSelectedState] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    const v = initParams().get('view'); return VALID_VIEWS.has(v) ? v : 'cards';
  });
  const [sortBy, setSortBy] = useState(() => {
    const v = initParams().get('sort'); return VALID_SORTS.has(v) ? v : 'state';
  });
  const [groupByState, setGroupByState] = useState(false);

  // ── Sync state → URL ───────────────────────────────────────────────────
  useEffect(() => {
    const p = new URLSearchParams();
    if (activeTab !== 'all') p.set('tab', activeTab);
    if (selectedStatus !== 'all') p.set('status', selectedStatus);
    if (searchTerm) p.set('q', searchTerm);
    if (viewMode !== 'cards') p.set('view', viewMode);
    if (sortBy !== 'state') p.set('sort', sortBy);
    const qs = p.toString();
    if (initialEntity) {
      const stillOnEntity = activeTab === initialEntity.tab && searchTerm === initialEntity.q && selectedStatus === 'all' && viewMode === 'cards' && sortBy === 'state';
      if (stillOnEntity) return;
      window.history.replaceState(null, '', qs ? `/?${qs}` : '/');
      return;
    }
    window.history.replaceState(null, '', qs ? `?${qs}` : window.location.pathname);
  }, [activeTab, selectedStatus, searchTerm, viewMode, sortBy]);

  const filteredData = useMemo(() => {
    let data = [];

    if (activeTab === 'all') {
      data = [...allStates, ...allInternationalLaws];
    } else if (activeTab === 'international') {
      data = allInternationalLaws;
    } else {
      data = statesData[activeTab] || [];
    }

    if (selectedStatus !== 'all') {
      data = data.filter((item) => item.status === selectedStatus);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      data = data.filter(
        (item) =>
          (item.state && item.state.toLowerCase().includes(term)) ||
          (item.country && item.country.toLowerCase().includes(term)) ||
          item.lawName.toLowerCase().includes(term) ||
          item.category.toLowerCase().includes(term)
      );
    }

    // Apply sorting
    const sortedData = [...data].sort((a, b) => {
      switch (sortBy) {
        case 'state':
          // Sort by state for US laws, country for international laws
          const aName = a.state || a.country || '';
          const bName = b.state || b.country || '';
          return aName.localeCompare(bName);
        case 'effectiveDate':
          return new Date(a.effectiveDate) - new Date(b.effectiveDate);
        case 'status':
          return a.status.localeCompare(b.status);
        case 'category':
          return a.category.localeCompare(b.category);
        default:
          return 0;
      }
    });

    return sortedData;
  }, [activeTab, selectedStatus, searchTerm, sortBy]);

  // Group data by state/country if enabled
  const groupedData = useMemo(() => {
    if (!groupByState) return null;

    const groups = {};
    filteredData.forEach((item) => {
      const groupKey = item.state || item.country;
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(item);
    });

    return Object.keys(groups)
      .sort()
      .map((name) => ({
        state: name, // Keep the key name for backward compatibility
        laws: groups[name]
      }));
  }, [filteredData, groupByState]);

  const stats = useMemo(() => {
    const allLaws = [...allStates, ...allInternationalLaws];
    const active = allLaws.filter((s) => s.status === 'active').length;
    const challenged = allLaws.filter((s) => s.status === 'challenged').length;
    const pending = allLaws.filter((s) => s.status === 'pending').length;
    const blocked = allLaws.filter((s) => s.status === 'blocked').length;

    return { active, challenged, pending, blocked, total: allLaws.length };
  }, []);

  return (
    <div className="min-h-screen">
      <ToolsBanner />
      <Header />
      <Stats stats={stats} />
      <UpcomingWatchlist />
      <SearchFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
      />
      <CategoryTabs activeTab={activeTab} setActiveTab={setActiveTab} />
      <CompanionResources activeTab={activeTab} />
      <ViewModeSelector
        viewMode={viewMode}
        setViewMode={setViewMode}
        resultCount={filteredData.length}
        onExport={() => exportToCSV(
          filteredData,
          `childrens-privacy-laws-${activeTab}-${new Date().toISOString().slice(0,10)}.csv`
        )}
      />

      {/* Show sorting controls for cards and table views */}
      {(viewMode === 'cards' || viewMode === 'table') && (
        <SortingControls
          sortBy={sortBy}
          setSortBy={setSortBy}
          groupByState={groupByState}
          setGroupByState={setGroupByState}
        />
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {viewMode === 'cards' && (
          <>
            {groupByState && groupedData ? (
              // Grouped view
              <div className="space-y-8">
                {groupedData.map((group, groupIdx) => (
                  <div key={groupIdx}>
                    <div className="flex items-center mb-4">
                      <h3 className="text-2xl font-bold text-gray-900">{group.state}</h3>
                      <div className="flex-1 h-0.5 bg-gradient-to-r from-purple-400 to-transparent ml-4"></div>
                      <span className="ml-4 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold">
                        {group.laws.length} {group.laws.length === 1 ? 'law' : 'laws'}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {group.laws.map((state, idx) => (
                        <StateCard key={idx} state={state} onClick={setSelectedState} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : activeTab === 'all' && filteredData.length > 0 ? (
              // All Laws view with US/International separation
              <>
                {/* US Laws Section */}
                {filteredData.filter(law => law.state).length > 0 && (
                  <div className="mb-12">
                    <div className="flex items-center mb-6">
                      <div className="bg-gradient-to-r from-blue-600 to-blue-400 text-white px-6 py-3 rounded-lg font-bold text-lg">
                        <i className="fas fa-flag-usa mr-2"></i>
                        United States Laws
                      </div>
                      <div className="flex-1 h-0.5 bg-gradient-to-r from-blue-400 to-transparent ml-4"></div>
                      <span className="ml-4 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                        {filteredData.filter(law => law.state).length} {filteredData.filter(law => law.state).length === 1 ? 'law' : 'laws'}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {filteredData.filter(law => law.state).map((state, idx) => (
                        <StateCard key={idx} state={state} onClick={setSelectedState} />
                      ))}
                    </div>
                  </div>
                )}

                {/* International Laws Section */}
                {filteredData.filter(law => law.country).length > 0 && (
                  <div>
                    <div className="flex items-center mb-6">
                      <div className="bg-gradient-to-r from-green-600 to-green-400 text-white px-6 py-3 rounded-lg font-bold text-lg">
                        <i className="fas fa-globe-americas mr-2"></i>
                        International Laws
                      </div>
                      <div className="flex-1 h-0.5 bg-gradient-to-r from-green-400 to-transparent ml-4"></div>
                      <span className="ml-4 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                        {filteredData.filter(law => law.country).length} {filteredData.filter(law => law.country).length === 1 ? 'law' : 'laws'}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {filteredData.filter(law => law.country).map((state, idx) => (
                        <StateCard key={idx} state={state} onClick={setSelectedState} />
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              // Regular view for specific category tabs
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredData.map((state, idx) => (
                  <StateCard key={idx} state={state} onClick={setSelectedState} />
                ))}
              </div>
            )}
          </>
        )}

        {viewMode === 'table' && (
          <>
            {activeTab === 'all' && filteredData.length > 0 ? (
              // All Laws table view with US/International separation
              <>
                {/* US Laws Section */}
                {filteredData.filter(law => law.state).length > 0 && (
                  <div className="mb-12">
                    <div className="flex items-center mb-6">
                      <div className="bg-gradient-to-r from-blue-600 to-blue-400 text-white px-6 py-3 rounded-lg font-bold text-lg">
                        <i className="fas fa-flag-usa mr-2"></i>
                        United States Laws
                      </div>
                      <div className="flex-1 h-0.5 bg-gradient-to-r from-blue-400 to-transparent ml-4"></div>
                      <span className="ml-4 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                        {filteredData.filter(law => law.state).length} {filteredData.filter(law => law.state).length === 1 ? 'law' : 'laws'}
                      </span>
                    </div>
                    <TableView filteredData={filteredData.filter(law => law.state)} setSelectedState={setSelectedState} />
                  </div>
                )}

                {/* International Laws Section */}
                {filteredData.filter(law => law.country).length > 0 && (
                  <div>
                    <div className="flex items-center mb-6">
                      <div className="bg-gradient-to-r from-green-600 to-green-400 text-white px-6 py-3 rounded-lg font-bold text-lg">
                        <i className="fas fa-globe-americas mr-2"></i>
                        International Laws
                      </div>
                      <div className="flex-1 h-0.5 bg-gradient-to-r from-green-400 to-transparent ml-4"></div>
                      <span className="ml-4 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                        {filteredData.filter(law => law.country).length} {filteredData.filter(law => law.country).length === 1 ? 'law' : 'laws'}
                      </span>
                    </div>
                    <TableView filteredData={filteredData.filter(law => law.country)} setSelectedState={setSelectedState} />
                  </div>
                )}
              </>
            ) : (
              // Regular table view for specific category tabs
              <TableView filteredData={filteredData} setSelectedState={setSelectedState} />
            )}
          </>
        )}

        {viewMode === 'timeline' && (
          <>
            {activeTab === 'all' && filteredData.length > 0 ? (
              // All Laws timeline view with US/International separation
              <>
                {/* US Laws Section */}
                {filteredData.filter(law => law.state).length > 0 && (
                  <div className="mb-12">
                    <div className="flex items-center mb-6">
                      <div className="bg-gradient-to-r from-blue-600 to-blue-400 text-white px-6 py-3 rounded-lg font-bold text-lg">
                        <i className="fas fa-flag-usa mr-2"></i>
                        United States Laws
                      </div>
                      <div className="flex-1 h-0.5 bg-gradient-to-r from-blue-400 to-transparent ml-4"></div>
                      <span className="ml-4 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                        {filteredData.filter(law => law.state).length} {filteredData.filter(law => law.state).length === 1 ? 'law' : 'laws'}
                      </span>
                    </div>
                    <TimelineView filteredData={filteredData.filter(law => law.state)} setSelectedState={setSelectedState} />
                  </div>
                )}

                {/* International Laws Section */}
                {filteredData.filter(law => law.country).length > 0 && (
                  <div>
                    <div className="flex items-center mb-6">
                      <div className="bg-gradient-to-r from-green-600 to-green-400 text-white px-6 py-3 rounded-lg font-bold text-lg">
                        <i className="fas fa-globe-americas mr-2"></i>
                        International Laws
                      </div>
                      <div className="flex-1 h-0.5 bg-gradient-to-r from-green-400 to-transparent ml-4"></div>
                      <span className="ml-4 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                        {filteredData.filter(law => law.country).length} {filteredData.filter(law => law.country).length === 1 ? 'law' : 'laws'}
                      </span>
                    </div>
                    <TimelineView filteredData={filteredData.filter(law => law.country)} setSelectedState={setSelectedState} />
                  </div>
                )}
              </>
            ) : (
              // Regular timeline view for specific category tabs
              <TimelineView filteredData={filteredData} setSelectedState={setSelectedState} />
            )}
          </>
        )}

        {filteredData.length === 0 && (
          <div className="text-center py-12">
            <i className="fas fa-search text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No results found</h3>
            <p className="text-gray-500">Try adjusting your filters or search terms</p>
          </div>
        )}
      </div>

      <HowItWorks />
      <ResourcesSection />
      <Legend />
      <Footer />

      {selectedState && <StateModal state={selectedState} onClose={() => setSelectedState(null)} />}
      <FirstTimeModal />
    </div>
  );
}

export default App;
